/**
 * PremiumPackageItem
 *
 * Displays a single premium package card with name, propositions, price, and status button.
 * Matches Flutter: lib/presentation/screens/premium/widgets/premium_package_item.dart
 */
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import type { PremiumPackageEntity } from '@/domain/entities/premiumPackage/premiumPackageEntity';
import { InAppPurchaseStatus, isActiveStatus } from '@/domain/enums/premiumPackages/InAppPurchaseStatus';
import { useExpauseLocalizations } from '@/presentation/locale/expauseLocalizations';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface PremiumPackageItemProps {
  package: PremiumPackageEntity;
  status: InAppPurchaseStatus;
  onSubscribe: () => void;
  /**
   * `data-qa-id` prefix for the row + buy button. Defaults to `premium-package`;
   * the identity variant overrides it so each package keeps a single, distinct
   * qa id. Test-only; never affects rendering.
   */
  qaIdPrefix?: string;
}

export const PremiumPackageItem = ({ package: pkg, status, onSubscribe, qaIdPrefix = 'premium-package' }: PremiumPackageItemProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const { expauseLocalizations } = useExpauseLocalizations();

  const isActive = isActiveStatus(status);
  const isPending = status === InAppPurchaseStatus.pending;
  const buttonDisabled = isActive || isPending;

  // Readable status token for the qa-tester (enum name rather than numeric index).
  const statusToken = InAppPurchaseStatus[status] ?? String(status);

  return (
    <div
      {...qaAttr({ id: `${qaIdPrefix}-${pkg.productId}`, value: pkg.price })}
      style={{
        padding: `${responsive.spacing.xl}px ${responsive.spacing.base}px 0`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Package name */}
      <h2
        style={{
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.bold,
          color: theme.text.primary,
          textAlign: 'center',
          marginBottom: responsive.spacing.sm,
          margin: 0,
          paddingBottom: responsive.spacing.sm,
        }}
      >
        {pkg.name}
      </h2>

      {/* Card with propositions, price, and button */}
      <div
        style={{
          backgroundColor: theme.surface,
          borderRadius: responsive.borderRadius.md,
          padding: `${responsive.spacing.base}px ${responsive.spacing.xl}px`,
          width: '100%',
        }}
      >
        {pkg.propositions.map((proposition, index) => (
          <div key={index}>
            {/* Proposition text */}
            <p
              style={{
                fontSize: responsive.font.base,
                color: theme.text.secondary,
                textAlign: 'center',
                margin: 0,
              }}
            >
              {proposition}
            </p>

            {/* Separator */}
            <div
              style={{
                height: `${responsive.divider.small}px`,
                backgroundColor: theme.border,
                margin: `${responsive.spacing.base}px ${responsive.spacing.xs}px`,
              }}
            />

            {/* Price + button after last proposition */}
            {index === pkg.propositions.length - 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* Price */}
                <p
                  style={{
                    fontSize: responsive.font.base,
                    fontWeight: responsive.fontWeight.semibold,
                    color: theme.text.primary,
                    textAlign: 'center',
                    margin: 0,
                    paddingTop: responsive.spacing.lg,
                    paddingBottom: responsive.spacing.xs,
                  }}
                >
                  {pkg.price} {pkg.currency} {l10n.premiumPackages.monthly}
                </p>

                {/* Subscribe / Status button */}
                <button
                  {...qaAttr({ id: `${qaIdPrefix}-buy-${pkg.productId}`, status: statusToken })}
                  onClick={buttonDisabled ? undefined : onSubscribe}
                  style={{
                    backgroundColor: 'transparent',
                    border: `0.5px solid ${isActive ? theme.activeRed : theme.accent}`,
                    borderRadius: responsive.borderRadius.sm,
                    padding: `${responsive.spacing.sm}px ${responsive.spacing['3xl']}px`,
                    cursor: buttonDisabled ? 'default' : 'pointer',
                    opacity: buttonDisabled ? 0.8 : 1,
                  }}
                >
                  <span
                    style={{
                      fontSize: responsive.font.base,
                      fontWeight: responsive.fontWeight.semibold,
                      color: theme.text.primary,
                    }}
                  >
                    {expauseLocalizations.getInAppPurchaseStatus(status)}
                  </span>
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
